import { z } from "zod";
import { ROOM_PARTICIPANTS_MAX } from "./room.ts";

/**
 * Звонок с преподавателями: контракты ручек звонка
 * (`docs/teacher-calls-design.md`, 8.1 и 8.4).
 *
 * Звонок это урок без ученика (`Lesson.call`), поэтому создание отвечает
 * обычной строкой `Lesson`, а здесь только то, чего у урока нет: ссылка гостя
 * и её погашение.
 */

/**
 * Сколько гостей одновременно бывает в звонке: вместе с ведущим это
 * вместимость комнаты.
 *
 * ⚠️ **Выражение, а не литерал**: вместимость у системы одна
 * (`ROOM_PARTICIPANTS_MAX`, шапка в `room.ts`), и второе число запрещено той же
 * шапкой. Станет вместимость свойством преподавателя, потолок гостей поедет
 * вместе с ней.
 */
export const CALL_GUESTS_MAX = ROOM_PARTICIPANTS_MAX - 1;

/** Ключ ссылки гостя: 128 бит в base64url, ровно 22 символа (`CHECK` в базе). */
export const GuestLinkKey = z.string().regex(/^[A-Za-z0-9_-]{22}$/);
export type GuestLinkKey = z.infer<typeof GuestLinkKey>;

/** `POST /v1/calls`: название звонка, тот же предел, что у урока. */
export const CreateCallRequest = z.object({
  title: z.string().min(1).max(200),
});
export type CreateCallRequest = z.infer<typeof CreateCallRequest>;

/**
 * `POST /v1/lessons/:lessonId/guest-link`: готовая ссылка гостя,
 * `/call/<lessonId>#<ключ>`.
 *
 * ⚠️ Ключ во ФРАГМЕНТЕ адреса, как у приглашения преподавателя: фрагмент не
 * уходит на сервер ни при загрузке страницы, ни в журналы.
 */
export const GuestLinkResponse = z.object({
  url: z.string().url(),
});
export type GuestLinkResponse = z.infer<typeof GuestLinkResponse>;

/**
 * Погашение ссылки гостя. Ключ едет ТЕЛОМ, а не в пути, по той же причине, что
 * у ссылки ученика на занятие: путь попадает в журналы, тело нет. Форму ключа
 * проверяет сервер, и кривой ключ отвечает тем же отказом, что неверный.
 */
export const RedeemGuestLinkRequest = z.object({
  key: z.string().max(64),
});
export type RedeemGuestLinkRequest = z.infer<typeof RedeemGuestLinkRequest>;

/** Погашение прошло: звонок, в комнату которого теперь можно войти. */
export const RedeemGuestLinkResponse = z.object({
  lessonId: z.string().uuid(),
});
export type RedeemGuestLinkResponse = z.infer<typeof RedeemGuestLinkResponse>;
